import { adjacentBook, chapterCount } from './chapterSequence';

// ── The chapter scrubber's arithmetic ───────────────────────────────────────
// The track is one book wide and split into equal segments, one per page of
// the book: its outline page first, then each chapter, so every page is as
// easy to land on as any other. Pure functions, used by ChapterScrubber and
// tested on their own. chapterAt / fractionFor count segments 1…count;
// pageAt / pageFraction put the outline (ch null) in front of chapter 1.

// Where x falls along the track, 0…1 (clamped).
export function fractionAt(x, left, width) {
  if (!(width > 0)) return 0;
  return Math.min(1, Math.max(0, (x - left) / width));
}

// Where x falls when scrubbing starts from a held finger rather than a press
// on the track: the spot the finger started from (x0) stands for where the
// reader already is (f0), the track's left end for chapter 1 and its right
// end for the last, stretched linearly in between. So holding still changes
// nothing, and both ends are always within reach of the thumb.
export function anchoredFraction(x, x0, f0, left, width) {
  const right = left + width;
  if (x <= x0) {
    const span = x0 - left;
    return span <= 0 ? 0 : Math.max(0, f0 * (x - left) / span);
  }
  const span = right - x0;
  return span <= 0 ? 1 : Math.min(1, f0 + (1 - f0) * (x - x0) / span);
}

// The chapter whose segment holds fraction f, 1…count.
export function chapterAt(f, count) {
  if (count <= 1) return 1;
  return Math.min(count, Math.max(1, Math.floor(f * count) + 1));
}

// The middle of chapter ch's segment, for placing the thumb and bubble.
export function fractionFor(ch, count) {
  if (count <= 1) return 0.5;
  return (Math.min(count, Math.max(1, ch)) - 0.5) / count;
}

// The page whose segment holds fraction f on a book of `count` chapters:
// null for the outline page (the first segment), else the chapter.
export function pageAt(f, count) {
  return chapterAt(f, count + 1) - 1 || null;
}

// The middle of a page's segment; ch null (or 0) is the outline page.
export function pageFraction(ch, count) {
  return fractionFor((ch || 0) + 1, count + 1);
}

// -1 when x is at (or past) the track's left end, 1 at the right end, else 0.
// Holding the finger there carries the scrubber into the next book.
export function edgeAt(x, left, width, tolerance = 6) {
  if (x <= left + tolerance) return -1;
  if (x >= left + width - tolerance) return 1;
  return 0;
}

// Crossing out of `book` past its first (dir -1) or last (dir +1) page, or
// null at Genesis / Revelation. With the finger's fraction `f` the track now
// stands for the other book, so the page is whatever lies under the finger
// there (at the left end that is the outline, and holding on keeps going back
// a book at a time, like rewinding). Without it, the seam between the books:
// the previous book's last chapter or the next book's outline.
export function crossBook(book, dir, f) {
  const other = adjacentBook(book, dir);
  if (!other) return null;
  const count = chapterCount(other);
  if (f != null) return { book: other, ch: pageAt(f, count) };
  return { book: other, ch: dir > 0 ? null : count };
}

// Keyboard steps on the slider: stays inside the book (that is the slider's
// range, `min`…count, where the scrubber passes 0 for the outline page);
// PageUp/PageDown move ten, Home/End go to the ends.
export function keyStep(key, ch, count, min = 1) {
  switch (key) {
    case 'ArrowRight': case 'ArrowUp':   return Math.min(count, ch + 1);
    case 'ArrowLeft':  case 'ArrowDown': return Math.max(min, ch - 1);
    case 'PageUp':   return Math.min(count, ch + 10);
    case 'PageDown': return Math.max(min, ch - 10);
    case 'Home': return min;
    case 'End':  return count;
    default: return null;
  }
}
